/**
 * Global Error Boundary Component
 *
 * This error boundary catches all unhandled errors throughout the entire application.
 * It provides a user-friendly fallback UI with options to:
 * - Reload the current page
 * - Navigate back to the home/workspace page
 *
 * Usage: This should be placed at the highest level in the component tree (src/index.tsx)
 * to catch any errors that bubble up from anywhere in the application.
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Typography, Container, Paper } from "@mui/material";
import { BrightBotSmileIcon } from "../../common/Icons/BrightBotSmileIcon";
import { brighthiveTheme } from "../../theme/theme";

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Global Error Boundary caught an error:", error, errorInfo);

    // Log to Sentry if available
    if ((window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = "/workspace";
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="md" sx={{ mt: 8 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: "center",
              backgroundColor: brighthiveTheme.palette.common.white,
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 3 }}>
              <BrightBotSmileIcon
                style={{
                  fontSize: 64,
                  color: brighthiveTheme.palette.error.main,
                }}
              />
            </Box>

            <Typography variant="h4" component="h1" gutterBottom color="error">
              Oops! Something went wrong
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We encountered an unexpected error. Don't worry, this has been
              reported to our team.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleReload}
                size="large"
              >
                Reload Page
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={this.handleGoHome}
                size="large"
              >
                Go to Home
              </Button>
            </Box>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box sx={{ mt: 4, textAlign: "left" }}>
                <Typography variant="h6" gutterBottom>
                  Error Details (Development Mode):
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: "#f5f5f5",
                    p: 2,
                    borderRadius: 1,
                    overflow: "auto",
                    fontSize: "0.875rem",
                    maxHeight: "200px",
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Box>
              </Box>
            )}
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
